/**
 * Leader election across this browser's tabs, via the Web Locks API. One
 * tab holds the `vnext-leader` lock; it alone runs shell-level polls (and,
 * later, the SSE stream) and shares results over the tab channel.
 *
 * A hidden leader gives the lock up so a visible tab can take over — the
 * lock queue is FIFO, so the next visible tab waiting becomes leader —
 * and re-queues when it is foregrounded again. When every tab is hidden
 * nobody holds the lock and nobody polls, which is the point.
 *
 * Without `navigator.locks` every tab is its own leader (the behaviour
 * before coordination existed). Coordination must never break polling.
 *
 * One lock per tab regardless of how many hooks ask: the module keeps a
 * ref-counted singleton.
 */

import { useEffect, useState } from 'react';

import { isDocumentVisible, subscribeVisibility } from '@/lib/visibility';

const LOCK_NAME = 'vnext-leader';

type Listener = (leader: boolean) => void;

const listeners = new Set<Listener>();
let retained = 0;
let leader = false;
/** Resolves the promise that holds the lock. */
let release: (() => void) | null = null;
/** Aborts a request still waiting in the lock queue. */
let queued: AbortController | null = null;
let unsubscribeVisibility: (() => void) | null = null;

function lockManager(): LockManager | null {
  if (typeof navigator === 'undefined') {
    return null;
  }
  return (navigator as Navigator & { locks?: LockManager }).locks ?? null;
}

function setLeader(next: boolean) {
  if (leader === next) {
    return;
  }
  leader = next;
  for (const listener of [...listeners]) {
    listener(next);
  }
}

function acquire() {
  if (leader || queued) {
    return;
  }
  const manager = lockManager();
  if (!manager) {
    setLeader(true); // no coordination available: lead alone
    return;
  }
  const controller = new AbortController();
  queued = controller;
  manager
    .request(
      LOCK_NAME,
      { mode: 'exclusive', signal: controller.signal },
      () =>
        new Promise<void>((resolve) => {
          if (queued !== controller) {
            resolve(); // gave up while waiting — hand the lock straight on
            return;
          }
          queued = null;
          release = () => {
            release = null;
            setLeader(false);
            resolve();
          };
          setLeader(true);
        })
    )
    .catch(() => {
      // Aborted while queued (tab hidden or unmounted before the grant).
    })
    .finally(() => {
      if (queued === controller) {
        queued = null;
      }
    });
}

function relinquish() {
  if (queued) {
    queued.abort();
    queued = null;
  }
  release?.();
}

function retain() {
  retained += 1;
  if (retained > 1) {
    return;
  }
  unsubscribeVisibility = subscribeVisibility((visible) => {
    if (visible) {
      acquire();
    } else {
      relinquish();
    }
  });
  if (isDocumentVisible()) {
    acquire();
  }
}

function unretain() {
  retained -= 1;
  if (retained > 0) {
    return;
  }
  unsubscribeVisibility?.();
  unsubscribeVisibility = null;
  relinquish();
  setLeader(false);
}

/** True while this tab holds the leader lock (or no lock API exists). */
export function useTabLeader(enabled = true): boolean {
  const [isLeader, setIsLeader] = useState(() => enabled && leader);

  useEffect(() => {
    if (!enabled) {
      setIsLeader(false);
      return undefined;
    }
    listeners.add(setIsLeader);
    retain();
    setIsLeader(leader);
    return () => {
      listeners.delete(setIsLeader);
      unretain();
    };
  }, [enabled]);

  return enabled && isLeader;
}
